// スポット市場の札 (通常入札・ブロック入札) のステータス列が共通で使う区分の判定規則。
//
// spec AC-BSP-047 は「通常入札・ブロック入札・約定結果・オペレーション履歴の全画面で
// 同じ区分を使用する」ことを求める。 通常入札 (12 区分) とブロック入札 (11 区分、
// AC-BSP-049) の違いは「部分約定」 の有無だけなので、 判定規則の実体はここ 1 か所に
// 置き、 各入札種別は約定軸の enum を [[BidMatchOutcome]] へ写すだけにする
// (`spotBidStatusCategory.ts` / `blockBidStatusCategory.ts`)。 規則を変える時に
// 片方だけ直して区分がずれることを防ぐため。
//
// 「失効」 の確定値は通常入札 (`BiddingSpotBidStatus.EXPIRED`) とブロック入札
// (`BiddingBlockBidStatus.EXPIRED`、 グループの全 leg に同時に書く) が持つ (T-67、
// AC-BSP-011)。 backend の send worker (bid_send_worker.go) が、 送信失敗 (確定) の
// 時点で締切に達していて、 かつ JEPX に届いた可能性のある送信が無い (= 一度も
// 送っていない、 または最初の送信がはっきり拒否された) 申請の PENDING な子 SpotBid に
// 書き (AC-BSP-117。 発注組織が未登録で送らずに断られただけの札〈AC-IJM-009〉も
// 一度も送っていない札に含む)、 `spotBidStatusCategory.ts` が FAILED と同じ `failed` へ写すので、
// 確定値がある札は下の優先順位 1 で「失効」 になる。
// ただし確定値が書かれない経路が残るため、 締切時刻 (`isPastDeadline`、 呼び出し側が
// `marketPhase.ts` の `spotDeadlineAt` 等から算出する) と 承認軸・送信軸からの代理判定を
// 引き続き併用する:
//   1. 承認申請中などのまま締切到達 (AC-BSP-099)。 送信キューに乗っていない札は
//      send worker に一度も渡らない。 承認待ちは約定結果の同期が締切後に失効を書く
//      が、 書かれるまでの間と、 同期の対象にならない下書き・差戻しは確定値が無い
//      (JEPX へ一度も送っていないので失効で確かな札)
//   2. 送信失敗のまま締切到達した札 (AC-BSP-011 / AC-BSP-117)。 再送中 (SEND_FAILED) の
//      札は、 JEPX が受け付けていたかを確かめるまで締切後も「送信中」 のまま表示する
//      (下の優先順位 4)。 send worker の締切ガードは、 この札の再送を止めて送信軸を
//      SEND_FAILED_FINAL に確定するが、 返事が届かなかった送信がありうる札の子は
//      EXPIRED にしない。 その札は約定同期が JEPX に問い合わせて、 受け付けていたなら
//      送信軸を SENT に戻して約定待ちに、 届いていなければ子を EXPIRED にする。 確かめている
//      間は backend が `receipt_checking` を返すので、 締切後も 「送信中」 のまま表示する
//      (確かめる期限を過ぎると子を EXPIRED にする)。 締切前に eSLJ に拒否されて
//      SEND_FAILED_FINAL に確定した札も子は EXPIRED にならない。 確定値も
//      `receipt_checking` も無い SEND_FAILED_FINAL の札は、 締切後はこの代理判定で
//      「失効」 と表示するが、 失効が確定した訳ではないので backend は失効の確定
//      (`expiry_confirmed`) を返さず、 翌営業日向けの複製は出さない (`isExpiryConfirmed`)。
//      締切前は下の優先順位 3 のとおり、 下書き (確定済ワークフロー経由は失効)
// ブロック入札も同じ規則で、 確定値 (EXPIRED) と `receipt_checking` はグループ単位で決まる。
// 失効の確定値は、 締切まで提出しなかった下書き・承認待ち・差戻し (AC-BSP-099) と、 締切後に
// JEPX に届いていないと確かめたグループ (AC-BSP-117) に約定同期が書く。 確定値の書込みより古い
// グループ (約定同期の窓の外) には書かれないので、 代理判定が残る。 workflowId は持たないので、
// 締切前に送れなかった札は常に下書きになる。
//
// 代理判定の規則は mock-app の `BidStatusHelper.DetermineStatus` /
// `JepxSpotBid.Filters.cs` `GetBlockEffectiveStatus` と domain-knowledge
// 「失効 = JepxBidNo が空のまま締切到達」 に従う: 締切を過ぎて JEPX に届いていない札は、
// 取消を除き承認状態を問わず「失効」 にする。 JEPX 到達は backend
// `valueobject.ReachedJEPX` と同じく 送信軸 SENT で判定する (承認軸だけでは判定しない)。
import {
  BiddingApprovalState,
  BiddingBidSendStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

/** 部分約定を除いた 11 区分 (= ブロック入札の区分、 AC-BSP-049)。 */
export type BidStatusCategoryWithoutPartial =
  | 'reserved' // 予約中
  | 'draft' // 下書き
  | 'pendingApproval' // 承認待ち
  | 'rejected' // 差戻し
  | 'sending' // 送信中
  | 'active' // 入札中
  | 'awaitingMatch' // 約定待ち
  | 'allMatched' // 全約定
  | 'unmatched' // 未約定
  | 'cancelled' // 取消
  | 'expired' // 失効

/**
 * 区分名の i18n key。 入札一覧のステータス列 (バッジ) と、 入札一覧の CSV ダウンロードの
 * ステータス列が同じ表から引く (AC-BSP-047 / AC-BSP-049: 区分名は画面と一致させる)。
 * 通常入札は部分約定の key を足した [[SPOT_BID_STATUS_CATEGORY_LABEL_KEY]] を引く。
 */
export const BID_STATUS_CATEGORY_LABEL_KEY = {
  reserved: 'page:biddingSpot.bidStatusCategory.reserved',
  draft: 'page:biddingSpot.bidStatusCategory.draft',
  pendingApproval: 'page:biddingSpot.bidStatusCategory.pendingApproval',
  rejected: 'page:biddingSpot.bidStatusCategory.rejected',
  sending: 'page:biddingSpot.bidStatusCategory.sending',
  active: 'page:biddingSpot.bidStatusCategory.active',
  awaitingMatch: 'page:biddingSpot.bidStatusCategory.awaitingMatch',
  allMatched: 'page:biddingSpot.bidStatusCategory.allMatched',
  unmatched: 'page:biddingSpot.bidStatusCategory.unmatched',
  cancelled: 'page:biddingSpot.bidStatusCategory.cancelled',
  expired: 'page:biddingSpot.bidStatusCategory.expired',
} as const satisfies Record<BidStatusCategoryWithoutPartial, string>

/**
 * 約定軸を入札種別に依らない形へ正規化した値。
 *   - matched: 約定した (部分約定かどうかは呼び出し側が判定する)
 *   - unmatched: JEPX が評価した結果ゼロ
 *   - failed: eSLJ 側の JEPX 送信自体の失敗 (spec「eSLJ ステータス対応」表で失効)
 *   - pending: 約定結果が未確定
 */
export type BidMatchOutcome = 'matched' | 'unmatched' | 'failed' | 'pending'

export type BidStatusCategoryInput = {
  approvalState: BiddingApprovalState
  sendStatus?: BiddingBidSendStatus
  matchOutcome: BidMatchOutcome
  /**
   * JEPX が受け付けていたかを確かめている札か (backend が応答時点で決める
   * `receipt_checking`、 AC-BSP-117)。 真の間は締切の後も 「送信中」 のまま出す。
   * 通常入札は札単位、 ブロック入札はグループ単位の値。
   */
  receiptChecking?: boolean
  /**
   * 確定済ワークフロー経由の札か (札に workflowId がある)。 締切前に送れなかった札の区分が
   * 分かれる (AC-BSP-117): 経由していれば失効、 していなければ下書き。
   */
  hasWorkflow: boolean
}

/**
 * 約定軸が確定していない提出操作済 (承認軸 SUBMITTED)・承認済 (承認軸 APPROVED) の札が、
 * 送信軸から見て JEPX に届いたか。
 * 締切後の行の区分 ([[deriveBidStatusCategory]]) と承認状態バナーの見出し
 * (`approvalBannerHeading.ts`) は、この判定から各々の表示区分へ写すだけにする。 backend が
 * AC-BSP-117 の照会や失効の確定を実装した時は、 代理判定をここで 1 回だけ直す。
 *   - reached: 送信軸 SENT (backend `valueobject.ReachedJEPX` と同じ)
 *   - unconfirmed: 送信軸 SEND_FAILED (再送中)。 返事が届かなかった送信があり、 JEPX が受け付けて
 *     いた可能性がある
 *   - notReached: 一度も送っていない・送信失敗 (確定)。 失効の確定値が無い札では、 届いていないと
 *     確かめたのではなく代理判定
 * 写し方の違い: 行は unconfirmed を「送信中」、 notReached を「失効」 (代理判定) にする。
 * バナーは約定軸の失効が全件確定するまで、 どちらも「送信を確認できない」 (黄) にする
 * (AC-BSP-067、 Discussion #9417)。
 */
export type JepxReach = 'reached' | 'unconfirmed' | 'notReached'

export const deriveJepxReach = (
  sendStatus: BiddingBidSendStatus | undefined,
): JepxReach => {
  if (sendStatus === BiddingBidSendStatus.SENT) {
    return 'reached'
  }
  if (sendStatus === BiddingBidSendStatus.SEND_FAILED) {
    return 'unconfirmed'
  }
  return 'notReached'
}

// 締切前に送れなかった札 (AC-BSP-117): 内容の拒否・送信直前の再検証の違反・JEPX 会員 ID 未登録は
// backend が同じ札を下書きに戻し (PR #11921)、 確定済ワークフロー経由の札は失効にする。 その遷移の
// 後は承認軸が提出済でなくなるので、 この分岐には来ない。
// この分岐は次の札のために残す。 承認軸が提出済のまま送信軸だけが SEND_FAILED_FINAL になる札:
//   - PR #11921 のデプロイ前に締切前で確定失敗になった札 (backfill しない。 締切に失効する)
// 重複提出の応答 (ErrAlreadySubmitted) は ErrPermanentlyRejected を付けないので SEND_FAILED
// (再送中) になり、 この分岐には来ない (bid_send_worker.go:1239 の permanent 判定)。
// 上の札が締切で失効して尽きたら、 この分岐は消せる (#10680)。
const sendFailedBeforeDeadlineCategory = (
  hasWorkflow: boolean,
): BidStatusCategoryWithoutPartial => (hasWorkflow ? 'expired' : 'draft')

const submittedCategoryBeforeDeadline = (
  sendStatus: BiddingBidSendStatus | undefined,
  hasWorkflow: boolean,
): BidStatusCategoryWithoutPartial => {
  switch (sendStatus) {
    case BiddingBidSendStatus.RESERVED:
      return 'reserved'
    case BiddingBidSendStatus.SENT:
      return 'active'
    case BiddingBidSendStatus.SEND_FAILED_FINAL:
      return sendFailedBeforeDeadlineCategory(hasWorkflow)
    default:
      return 'sending'
  }
}

const PAST_DEADLINE_CATEGORY: Record<
  JepxReach,
  BidStatusCategoryWithoutPartial
> = {
  reached: 'awaitingMatch',
  unconfirmed: 'sending',
  notReached: 'expired',
}

/**
 * 承認軸 + 送信軸 + 約定軸 (正規化済み) から区分を 1 つ返す純関数。
 * 約定した札は 'allMatched' を返すので、 部分約定を持つ通常入札は呼び出し側で
 * 'partialMatched' へ細分する。
 *
 * 優先順位 (上から評価し、 最初にマッチした区分を返す):
 *   1. 約定軸が確定終端 (matched / unmatched / failed) — 約定同期が書く値は
 *      権威的なので、 承認軸・送信軸の状態に関わらず最優先で返す (backend
 *      `MarketFate` と同じ優先順位)。 failed は「失効」 に確定する
 *   2. 承認軸 CANCELLED — 取消・予約取消 (締切後も取消のまま)
 *   3. 締切前の区分:
 *      - DRAFT → 下書き / PENDING → 承認待ち / REJECTED → 差戻し
 *      - SUBMITTED / APPROVED → 送信軸 RESERVED なら予約中、 SENT (= JEPX が受け付けた)
 *        なら入札中、 それ以外 (未送信の承認済・送信待ち・送信中・再送中) は送信中
 *        (AC-BSP-047 / AC-BSP-014)。 送信失敗 (確定) の SEND_FAILED_FINAL と FAILED
 *        (JEPX 提出失敗、 legacy 値) は、 同じ札を下書きに戻す (AC-BSP-117) ので下書き、
 *        確定済ワークフロー経由 (workflowId あり) の札は失効
 *   4. 締切後の区分 ([[deriveJepxReach]] から写す): 送信軸 SENT (= JEPX 到達) の SUBMITTED / APPROVED だけが
 *      約定待ち。 backend が JEPX に受け付けていたかを確かめている札
 *      ([[BidStatusCategoryInput.receiptChecking]]) と、 送信軸 SEND_FAILED (一時的な障害で
 *      再送中 = 返事が届かなかった送信がある) の SUBMITTED / APPROVED は送信中のまま
 *      (AC-BSP-117: JEPX が受け付けていた可能性があり、 確かめられるまでは締切の時刻で
 *      失効にしない。 backend が失効を確定すれば 1. で失効に、 受け付けていたと確かめれば
 *      送信軸 SENT で約定待ちになる)。 それ以外の JEPX に届いていない札 (一度も送っていない・
 *      送信失敗 (確定) の SEND_FAILED_FINAL。 冒頭の 2. のとおり、 確定値の無い札を含む) は失効
 *   5. 上記いずれにも当たらない組み合わせ (承認軸 UNSPECIFIED 等、 実データでは
 *      起きない想定外値) — 表示を壊さないよう最も安全な「下書き」 に倒す
 */
export const deriveBidStatusCategory = (
  input: BidStatusCategoryInput,
  isPastDeadline: boolean,
): BidStatusCategoryWithoutPartial => {
  if (input.matchOutcome === 'matched') {
    return 'allMatched'
  }
  if (input.matchOutcome === 'unmatched') {
    return 'unmatched'
  }
  if (input.matchOutcome === 'failed') {
    return 'expired'
  }

  const { approvalState, sendStatus, hasWorkflow } = input
  if (approvalState === BiddingApprovalState.CANCELLED) {
    return 'cancelled'
  }
  if (approvalState === BiddingApprovalState.UNSPECIFIED) {
    return 'draft'
  }
  const isSubmitted =
    approvalState === BiddingApprovalState.SUBMITTED ||
    approvalState === BiddingApprovalState.APPROVED

  if (isPastDeadline) {
    if (!isSubmitted) {
      return 'expired'
    }
    // 確かめている間は締切の時刻で失効にしない (AC-BSP-117)。 確かめ終えると backend が
    // 送信軸を SENT (受け付けていた = 約定待ち) か約定軸を失効にする。
    if (input.receiptChecking === true) {
      return 'sending'
    }
    return PAST_DEADLINE_CATEGORY[deriveJepxReach(sendStatus)]
  }

  switch (approvalState) {
    case BiddingApprovalState.DRAFT:
      return 'draft'
    case BiddingApprovalState.PENDING:
      return 'pendingApproval'
    case BiddingApprovalState.REJECTED:
      return 'rejected'
    case BiddingApprovalState.FAILED:
      return sendFailedBeforeDeadlineCategory(hasWorkflow)
    default:
      break
  }
  if (isSubmitted) {
    return submittedCategoryBeforeDeadline(sendStatus, hasWorkflow)
  }
  return 'draft'
}

/**
 * 発注主体区分が空欄の札を入札後ポジションの別項目に数える区分 (AC-BSP-127、 #10084)。
 * 取消・失効は数えない。
 */
export const UNKNOWN_ISSUER_COUNTED_CATEGORIES: ReadonlySet<
  BidStatusCategoryWithoutPartial | 'partialMatched'
> = new Set([
  'active',
  'awaitingMatch',
  'allMatched',
  'partialMatched',
  'unmatched',
])

/**
 * 一括作成の「追加」 で差し引く既存入札量に入れる区分 (AC-BSP-005a)。 AC-BSP-047 の
 * 進行中 (下書き / 承認待ち / 差戻し / 予約中 / 送信中 / 入札中 /
 * 約定待ち) と、 約定量を数える全約定・部分約定。 区分はステータス列と同じ導出
 * (`deriveSpotBidStatusCategory` / `deriveBlockBidStatusCategory`) から引く
 * (承認軸・送信軸・約定軸から組み直すと、 一覧の区分と差し引く量がずれるため)。
 */
export const BULK_ADD_COUNTED_CATEGORIES: ReadonlySet<
  BidStatusCategoryWithoutPartial | 'partialMatched'
> = new Set([
  'draft',
  'pendingApproval',
  'rejected',
  'reserved',
  'sending',
  'active',
  'awaitingMatch',
  'allMatched',
  'partialMatched',
])
